import { Component, createRef } from "react";
import teamAvatars from "./teamAvatars.js";

export default class DashboardTeamMatchups extends Component {
  constructor(props) {
    super(props);
    this.listRef = createRef();
    this.carouselRef = createRef();
    this.cardRef = createRef();
    this.inactivityTimer = null;
    this.state = {
      isOverflowing: false,
      isActive: true,
    };
  }

  componentDidMount() {
    this.checkOverflow();
    window.addEventListener("resize", this.checkOverflow);
    if (this.cardRef.current) {
      this.cardRef.current.addEventListener("mousemove", this.handleMouseMove);
      this.cardRef.current.addEventListener("mouseenter", this.handleMouseMove);
    }
    this.startInactivityTimer();
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.checkOverflow);
    if (this.cardRef.current) {
      this.cardRef.current.removeEventListener(
        "mousemove",
        this.handleMouseMove,
      );
      this.cardRef.current.removeEventListener(
        "mouseenter",
        this.handleMouseMove,
      );
    }
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
    }
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.currentLeague !== this.props.currentLeague ||
      prevProps.currentWeek !== this.props.currentWeek
    ) {
      this.checkOverflow();
    }
  }

  checkOverflow = () => {
    if (this.listRef.current && this.carouselRef.current) {
      const listHeight = this.listRef.current.clientHeight;
      const carouselHeight = this.state.isOverflowing
        ? this.carouselRef.current.scrollHeight / 2
        : this.carouselRef.current.scrollHeight;
      const isOverflowing = carouselHeight > listHeight;
      if (this.state.isOverflowing !== isOverflowing) {
        this.setState({ isOverflowing });
      }
    }
  };

  handleMouseMove = () => {
    if (!this.state.isActive) {
      this.setState({ isActive: true });
    }
    this.startInactivityTimer();
  };

  startInactivityTimer = () => {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
    }
    this.inactivityTimer = setTimeout(() => {
      this.setState({ isActive: false });
    }, 10000);
  };

  renderTeamAvatar(team) {
    if (!team || team === "average") {
      return (
        <img
          src={teamAvatars["avg"]}
          alt="Average"
          className="matchup-avatar-img"
        />
      );
    }
    const avatarSrc =
      team.avatarId && teamAvatars[team.avatarId]
        ? teamAvatars[team.avatarId]
        : null;
    if (avatarSrc) {
      return (
        <img
          src={avatarSrc}
          alt={team.teamName}
          className="matchup-avatar-img"
        />
      );
    }
    return (
      <div className="matchup-avatar-placeholder">
        {team.teamName?.charAt(0) || "?"}
      </div>
    );
  }

  getTeamMatch = (week) => {
    const { currentLeague, teamSelected } = this.props;
    const weekMatches = currentLeague.matchPlan[week] || [];

    for (const match of weekMatches) {
      if (match === "average") {
        return "average";
      }
      if (match[0] == teamSelected.id || match[1] == teamSelected.id) {
        return match;
      }
    }
    return "average";
  };

  renderMatchupForWeek(week, keyPrefix = "") {
    const { currentLeague, teamSelected } = this.props;
    const match = this.getTeamMatch(week);

    if (match === "average") {
      const avgPoints = this.props.getWeekAveragePointsLeagueTeams(
        week,
        currentLeague.teams,
      );
      const teamPoints = this.props.getTeamAveragePoints(teamSelected, week);
      const teamWins = teamPoints >= avgPoints;

      const totalPoints = teamPoints + avgPoints;
      let greenPercent = 50;
      if (totalPoints > 0) {
        const winnerPoints = teamWins ? teamPoints : avgPoints;
        const ratio = winnerPoints / totalPoints;
        greenPercent = Math.min(67, Math.max(33, ratio * 100));
      }

      const gradientStyle = teamWins
        ? {
            background: `linear-gradient(115deg, #5da970 0%, #5da970 ${greenPercent}%, #f9fafb ${greenPercent}%, #f9fafb 100%)`,
          }
        : {
            background: `linear-gradient(65deg, #f9fafb 0%, #f9fafb ${
              100 - greenPercent
            }%, #5da970 ${100 - greenPercent}%, #5da970 100%)`,
          };

      return (
        <div
          className="matchup-item"
          key={`${keyPrefix}avg-${teamSelected.id}-${week}`}
          style={gradientStyle}
        >
          <div className="matchup-week-badge">UGE {week}</div>
          <div
            className={`matchup-team matchup-team-left ${
              teamWins ? "is-winner" : "is-loser"
            }`}
          >
            <div className="matchup-team-info">
              <div className="matchup-top-row">
                <div className="matchup-avatar">
                  {this.renderTeamAvatar(teamSelected)}
                </div>
                <span className="matchup-points">{teamPoints.toFixed(0)}</span>
              </div>
              <span className="matchup-team-name">{teamSelected.teamName}</span>
            </div>
          </div>
          <div
            className={`matchup-team matchup-team-right ${
              !teamWins ? "is-winner" : "is-loser"
            }`}
          >
            <div className="matchup-team-info">
              <div className="matchup-top-row">
                <span className="matchup-points">{avgPoints}</span>
                <div className="matchup-avatar">
                  {this.renderTeamAvatar("average")}
                </div>
              </div>
              <span className="matchup-team-name">Gennemsnittet</span>
            </div>
          </div>
        </div>
      );
    } else {
      const teamOne = teamSelected;
      const opponentId = teamSelected.id == match[0] ? match[1] : match[0];
      const teamTwo = this.props.findTeam(opponentId, currentLeague.id);

      let team1Points = this.props.getTeamAveragePoints(teamOne, week);
      let team2Points =
        teamTwo === "average"
          ? this.props.getWeekAveragePointsLeagueTeams(
              week,
              currentLeague.teams,
            )
          : this.props.getTeamAveragePoints(teamTwo, week);

      const team1Wins = team1Points >= team2Points;

      const totalPoints = team1Points + team2Points;
      let greenPercent = 50;
      if (totalPoints > 0) {
        const winnerPoints = team1Wins ? team1Points : team2Points;
        const ratio = winnerPoints / totalPoints;
        greenPercent = Math.min(67, Math.max(33, ratio * 100));
      }

      const gradientStyle = team1Wins
        ? {
            background: `linear-gradient(115deg, #5da970 0%, #5da970 ${greenPercent}%, #f9fafb ${greenPercent}%, #f9fafb 100%)`,
          }
        : {
            background: `linear-gradient(65deg, #f9fafb 0%, #f9fafb ${
              100 - greenPercent
            }%, #5da970 ${100 - greenPercent}%, #5da970 100%)`,
          };

      return (
        <div
          className="matchup-item"
          key={`${keyPrefix}${match[0]}-${match[1]}-${week}`}
          style={gradientStyle}
        >
          <div className="matchup-week-badge">UGE {week}</div>
          <div
            className={`matchup-team matchup-team-left ${
              team1Wins ? "is-winner" : "is-loser"
            }`}
          >
            <div className="matchup-team-info">
              <div className="matchup-top-row">
                <div className="matchup-avatar">
                  {this.renderTeamAvatar(teamOne)}
                </div>
                <span className="matchup-points">{team1Points.toFixed(0)}</span>
              </div>
              <span className="matchup-team-name">
                {teamOne?.teamName || "Unknown"}
              </span>
            </div>
          </div>
          <div
            className={`matchup-team matchup-team-right ${
              !team1Wins ? "is-winner" : "is-loser"
            }`}
          >
            <div className="matchup-team-info">
              <div className="matchup-top-row">
                <span className="matchup-points">{team2Points.toFixed(0)}</span>
                <div className="matchup-avatar">
                  {this.renderTeamAvatar(teamTwo)}
                </div>
              </div>
              <span className="matchup-team-name">
                {teamTwo === "average"
                  ? "Gennemsnittet"
                  : teamTwo?.teamName || "Unknown"}
              </span>
            </div>
          </div>
        </div>
      );
    }
  }

  render() {
    const { currentLeague, currentWeek } = this.props;

    // Collect all weeks up to currentWeek
    const weeks = Object.keys(currentLeague.matchPlan)
      .map(Number)
      .filter((w) => w <= currentWeek)
      .sort((a, b) => b - a);

    const LiveDot = () => {
      return (
        <svg height="25" width="25" className="blinking">
          <circle cx="12" cy="12" r="6" fill="crimson" />
        </svg>
      );
    };

    const renderAllMatchups = (keyPrefix = "") =>
      weeks.map((week) => this.renderMatchupForWeek(week, keyPrefix));

    return (
      <div className="matchup-card" ref={this.cardRef}>
        <div className="matchup-header align-center">
          <div className="matchup-title">
            <LiveDot />
            <h1>Kampe</h1>
          </div>
        </div>

        <div className="matchup-list" ref={this.listRef}>
          <div
            className={`matchup-carousel ${this.state.isOverflowing ? "" : "no-scroll"}`}
            ref={this.carouselRef}
          >
            {renderAllMatchups()}
            {this.state.isOverflowing && renderAllMatchups("dup-")}
          </div>
        </div>
      </div>
    );
  }
}
